import { Logger } from '@/Logger';

import { TeoError } from '../../errors/TeoError';
import type {
  AiQuickSuggestionsData,
  EditHistoryStep,
  HistoryIndexScore,
  RequestOptions,
  SuggestionPreset,
} from '../../TeoApi';
import type { AnalysisResult } from '../AnalysisResult';
import type { TeoText } from '../TeoText';
import type { AIHandler } from './AIHandler';

const LOG = Logger('quickSuggestionRequest');

/** A paragraph as it is sent to the API, together with the marker it came from. */
export type RequestedParagraph = {
  id: string;
  markerId: string;
  index: number;
  text: string;
};

/** Everything a quick request is made of, collected from the analysis and the editor. */
export type QuickRequest = {
  /** The analysis the marker ids belong to, which they are only valid for. */
  analysis: AnalysisResult;
  preset?: SuggestionPreset;
  instruction?: string;
  paragraphs: RequestedParagraph[];
  /** Full text at the start of the current auto session. */
  original_text?: string;
  /** TPI of {@link original_text}. */
  original_tpi?: number;
  /** Used analysis indices of {@link original_text}, in profile order. */
  original_indices?: HistoryIndexScore[];
  /** Applied rewrites since {@link original_text}, oldest first. */
  edit_history?: EditHistoryStep[];
};

/** A prompt a caller can send instead of the one the current instruction names. */
export type QuickRequestPrompt = {
  preset?: SuggestionPreset;
  instruction?: string;
};

/**
 * True once the editor hands out the paragraphs of the current analysis, which it only does
 * after it has attached its markers.
 */
export function hasEditorParagraphs(text: TeoText): boolean {
  const analysis = text.analysis;
  if (!analysis || !text.markers.canReadMarkerContents) return false;
  return collectParagraphs(text, analysis).length > 0;
}

/** The preset the current instruction asks for, or null while no instruction names one. */
export function quickSuggestionPreset(text: TeoText): SuggestionPreset | null {
  return text.analysis?.currentInstruction?.ai_preset ?? null;
}

/**
 * Collects what a quick request needs, or null when the text cannot provide it.
 *
 * The paragraphs are identified by the analysis, but their content is read from the editor,
 * so a request always reflects what the user currently sees.
 *
 * @param prompt When given, this is the preset and instruction that are sent, instead of
 * the preset of the current instruction. At least one of the two has to be present.
 */
export function prepareQuickRequest(
  text: TeoText,
  prompt?: QuickRequestPrompt,
): QuickRequest | null {
  const override = prompt !== undefined;
  const preset = override ? prompt.preset : (quickSuggestionPreset(text) ?? undefined);
  const instruction = prompt?.instruction;
  if (!preset && !instruction) {
    LOG.warn('Neither a preset nor an instruction is available for a quick suggestion.');
    return null;
  }
  const analysis = text.analysis;
  if (!analysis) {
    LOG.warn('No analysis available. Cannot determine the paragraphs of the text.');
    return null;
  }
  if (!text.markers.canReadMarkerContents) {
    LOG.warn('No editor available. Cannot read the paragraph contents.');
    return null;
  }
  const paragraphs = collectParagraphs(text, analysis);
  if (paragraphs.length === 0) {
    LOG.warn('No paragraphs with content found.');
    return null;
  }
  return { analysis, preset, instruction, paragraphs };
}

/**
 * Sends all paragraphs of the text at once and lets the AI choose which of them to improve,
 * which is why the response usually covers only some of them.
 * @param omitAnalysis Lets the backend skip the analysis of every suggestion. Faster, but
 * the response then holds no TPI to compare the suggestions by.
 * @param signal Aborts the request, which makes this throw like any other failure.
 * @throws When the request fails.
 */
export async function sendQuickRequest(
  text: TeoText,
  ai: AIHandler,
  request: QuickRequest,
  { omitAnalysis, signal }: { omitAnalysis?: boolean } & RequestOptions = {},
): Promise<AiQuickSuggestionsData> {
  const response = await text.core.api.getAiQuickSuggestions(
    {
      ...ai.getRequestBase(),
      ...(request.preset ? { preset: request.preset } : {}),
      ...(request.instruction ? { instruction: request.instruction } : {}),
      paragraphs: request.paragraphs.map(({ id, text: paragraphText }) => ({
        id,
        text: paragraphText,
      })),
      ...(request.edit_history?.length ?
        {
          original_text: request.original_text,
          original_tpi: request.original_tpi,
          original_indices: request.original_indices,
          edit_history: request.edit_history,
        }
      : {}),
      omit_analysis: omitAnalysis,
      debug: text.core.dynamicConfig.options.debugPermissions,
    },
    { signal },
  );
  if (TeoError.isError(response)) {
    throw new Error(`API error: ${response.message}`);
  }
  if (response.debug) {
    ai.logQuickPrompt({ prompt: response.debug.prompt, preset: request.preset });
  }
  return response;
}

function collectParagraphs(text: TeoText, analysis: AnalysisResult): RequestedParagraph[] {
  const markerIds = analysis.getParagraphMarkerIds();
  return text.markers
    .getMarkerContents(markerIds)
    .filter(({ text: paragraphText }) => paragraphText.trim().length > 0)
    .map(({ markerId, text: paragraphText }, index) => ({
      id: String(index),
      markerId,
      index,
      text: paragraphText,
    }));
}
